<p-toast />

<div class="mb-4">
    <div class="font-semibold text-2xl tracking-tight">Fake review authors</div>
    <div class="text-sm text-muted-color mt-1">
        Global pool of fake authors used by the platform when generating fake
        reviews.
    </div>
</div>

<div class="card">
    <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
        <p-tablist>
            <p-tab value="add">
                <span class="flex items-center gap-2">
                    <i class="pi pi-plus"></i> Add new
                </span>
            </p-tab>
            <p-tab value="manage">
                <span class="flex items-center gap-2">
                    <i class="pi pi-pencil"></i> Manage
                </span>
            </p-tab>
        </p-tablist>
        <p-tabpanels>
            <p-tabpanel value="add">
                @if (error()) {
                <p-message
                    severity="error"
                    [text]="error()!"
                    styleClass="mb-3"
                />
                }

                <p-table
                    [value]="rows()"
                    dataKey="id"
                    [rowTrackBy]="trackByRowId"
                    [showGridlines]="true"
                    responsiveLayout="scroll"
                    [paginator]="rows().length > pageSize()"
                    [rows]="pageSize()"
                    [first]="firstIndex()"
                    [rowsPerPageOptions]="pageSizeOptions"
                    (onPage)="onPage($event)"
                >
                    <ng-template #header>
                        <tr>
                            <th style="width: 30rem">Username *</th>
                            <th>Avatar URL</th>
                            <th style="width: 4rem"></th>
                        </tr>
                    </ng-template>
                    <ng-template #body let-row>
                        <tr>
                            <td>
                                <input
                                    pInputText
                                    type="text"
                                    class="w-full"
                                    placeholder="Username"
                                    [ngModel]="row.Username"
                                    (ngModelChange)="row.Username = $event ?? ''"
                                />
                            </td>
                            <td>
                                <div class="flex items-center gap-2">
                                    <div
                                        class="w-8 h-8 shrink-0 rounded-full overflow-hidden border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 flex items-center justify-center"
                                    >
                                        @if (row.AvatarUrl.trim()) {
                                        <img
                                            [src]="row.AvatarUrl.trim()"
                                            alt="Avatar preview"
                                            class="w-full h-full object-cover"
                                            referrerpolicy="no-referrer"
                                            (error)="onAvatarError($event)"
                                        />
                                        } @else {
                                        <i
                                            class="pi pi-user text-muted-color text-xs"
                                        ></i>
                                        }
                                    </div>
                                    <input
                                        pInputText
                                        type="text"
                                        class="flex-1"
                                        placeholder="https://… (optional)"
                                        [ngModel]="row.AvatarUrl"
                                        (ngModelChange)="row.AvatarUrl = $event ?? ''"
                                    />
                                </div>
                            </td>
                            <td>
                                <p-button
                                    icon="pi pi-trash"
                                    severity="danger"
                                    [text]="true"
                                    size="small"
                                    pTooltip="Remove row"
                                    (onClick)="requestRemoveRow(row)"
                                />
                            </td>
                        </tr>
                    </ng-template>
                </p-table>

                <div
                    class="flex items-center justify-between gap-2 mt-3 flex-wrap"
                >
                    <div class="flex items-center gap-2">
                        <p-button
                            label="Add row"
                            icon="pi pi-plus"
                            severity="secondary"
                            outlined
                            (onClick)="addRow()"
                        />
                        <p-button
                            label="Import JSON"
                            icon="pi pi-upload"
                            severity="secondary"
                            outlined
                            (onClick)="openImport()"
                        />
                        <p-button
                            label="Clear"
                            icon="pi pi-times"
                            severity="secondary"
                            [text]="true"
                            [disabled]="saving()"
                            (onClick)="requestClear()"
                        />
                    </div>
                    <div class="flex items-center gap-3">
                        <span class="text-sm text-muted-color">
                            Total rows: <strong>{{ rows().length }}</strong> ·
                            Ready to save: <strong>{{ validCount() }}</strong>
                        </span>
                        <p-button
                            label="Save authors"
                            icon="pi pi-check"
                            [loading]="saving()"
                            [disabled]="validCount() === 0"
                            (onClick)="save()"
                        />
                    </div>
                </div>
            </p-tabpanel>

            <p-tabpanel value="manage">
                <div
                    class="flex items-center justify-between gap-2 mb-3 flex-wrap"
                >
                    <div class="flex items-center gap-2 flex-wrap">
                        <p-iconfield>
                            <p-inputicon styleClass="pi pi-search" />
                            <input
                                pInputText
                                type="text"
                                placeholder="Search by username…"
                                [ngModel]="manageSearch()"
                                (ngModelChange)="manageSearch.set($event ?? '')"
                            />
                        </p-iconfield>
                        <label
                            class="inline-flex items-center gap-2 cursor-pointer ml-2"
                        >
                            <p-checkbox
                                inputId="authors-active-only"
                                [binary]="true"
                                [ngModel]="manageActiveOnly()"
                                (ngModelChange)="manageActiveOnly.set($event)"
                            />
                            <span class="text-sm">Active only</span>
                        </label>
                    </div>
                    <div class="flex items-center gap-3">
                        <span class="text-sm text-muted-color">
                            Showing
                            <strong>{{ filteredAuthors().length }}</strong>
                            of
                            <strong>{{ allAuthors().length }}</strong>
                        </span>
                        <p-button
                            label="Refresh"
                            icon="pi pi-refresh"
                            severity="secondary"
                            [text]="true"
                            size="small"
                            [loading]="manageLoading()"
                            (onClick)="reloadAuthors()"
                        />
                    </div>
                </div>

                @if (manageError()) {
                <p-message
                    severity="error"
                    [text]="manageError()!"
                    styleClass="mb-3"
                />
                }

                <p-table
                    [value]="filteredAuthors()"
                    dataKey="Id"
                    [showGridlines]="true"
                    responsiveLayout="scroll"
                    [loading]="manageLoading()"
                    [paginator]="true"
                    [rows]="20"
                    [rowsPerPageOptions]="[10, 20, 50, 100]"
                >
                    <ng-template #header>
                        <tr>
                            <th style="width: 4rem">Avatar</th>
                            <th>Username</th>
                            <th style="width: 8rem">Active</th>
                            <th style="width: 6rem"></th>
                        </tr>
                    </ng-template>
                    <ng-template #body let-author>
                        <tr>
                            <td>
                                <div
                                    class="w-8 h-8 shrink-0 rounded-full overflow-hidden border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 flex items-center justify-center"
                                >
                                    @if (author.AvatarUrl) {
                                    <img
                                        [src]="author.AvatarUrl"
                                        alt="Avatar"
                                        class="w-full h-full object-cover"
                                        referrerpolicy="no-referrer"
                                        (error)="onAvatarError($event)"
                                    />
                                    } @else {
                                    <i
                                        class="pi pi-user text-muted-color text-xs"
                                    ></i>
                                    }
                                </div>
                            </td>
                            <td>
                                <span
                                    [class.text-muted-color]="!author.IsActive"
                                >
                                    {{ author.Username }}
                                </span>
                            </td>
                            <td>
                                <p-checkbox
                                    [binary]="true"
                                    [ngModel]="author.IsActive"
                                    [disabled]="
                                        togglingId() !== null &&
                                        togglingId() !== author.Id
                                    "
                                    (ngModelChange)="
                                        toggleActive(author, $event)
                                    "
                                />
                            </td>
                            <td>
                                <p-button
                                    icon="pi pi-pencil"
                                    severity="secondary"
                                    [text]="true"
                                    size="small"
                                    pTooltip="Edit"
                                    (onClick)="openEdit(author)"
                                />
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template #emptymessage>
                        <tr>
                            <td
                                colspan="4"
                                class="text-center text-muted-color py-4"
                            >
                                @if (manageLoading()) { Loading… } @else if
                                (allAuthors().length === 0) { No authors. }
                                @else { No matches. }
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
            </p-tabpanel>
        </p-tabpanels>
    </p-tabs>
</div>

<p-dialog
    [visible]="importDialogVisible()"
    (visibleChange)="importDialogVisible.set($event)"
    header="Import authors (JSON)"
    [modal]="true"
    [style]="{ width: '48rem', maxWidth: '95vw' }"
    [draggable]="false"
    [resizable]="false"
>
    <div class="flex flex-col gap-3">
        <p class="text-sm text-muted-color m-0">
            Paste a JSON array, drop a <code>.json</code> file, or pick one.
            Parsed rows are appended to the table — review and click Save to
            insert into the pool.
        </p>

        <div
            class="rounded border-2 border-dashed p-2 transition-colors"
            [class.border-primary-500]="importDragOver()"
            [class.bg-primary-50]="importDragOver()"
            [class.border-surface-300]="!importDragOver()"
            [class.dark:border-surface-600]="!importDragOver()"
            (dragover)="onImportDragOver($event)"
            (dragleave)="onImportDragLeave()"
            (drop)="onImportDrop($event)"
        >
            <textarea
                pTextarea
                rows="12"
                class="w-full font-mono text-xs"
                spellcheck="false"
                [placeholder]="importExample"
                [ngModel]="importJsonText()"
                (ngModelChange)="importJsonText.set($event ?? '')"
            ></textarea>
        </div>

        <div class="flex items-center justify-between gap-2 flex-wrap">
            <label class="inline-flex">
                <input
                    #fileInput
                    type="file"
                    accept="application/json,.json"
                    class="hidden"
                    (change)="onImportFileChange($event)"
                />
                <p-button
                    label="Load file…"
                    icon="pi pi-file"
                    severity="secondary"
                    outlined
                    size="small"
                    (onClick)="fileInput.click()"
                />
            </label>
            <span class="text-xs text-muted-color">
                Expected shape matches the example shown as placeholder.
            </span>
        </div>

        @if (importError()) {
        <p-message severity="error" [text]="importError()!" styleClass="m-0" />
        }
    </div>

    <ng-template #footer>
        <p-button
            label="Cancel"
            severity="secondary"
            [text]="true"
            (onClick)="closeImport()"
        />
        <p-button
            label="Import"
            icon="pi pi-check"
            (onClick)="confirmImport()"
        />
    </ng-template>
</p-dialog>

<p-dialog
    [visible]="clearConfirmVisible()"
    (visibleChange)="clearConfirmVisible.set($event)"
    header="Clear all rows?"
    [modal]="true"
    [style]="{ width: '28rem' }"
    [draggable]="false"
    [resizable]="false"
>
    <p class="m-0 text-sm">
        Discard {{ rows().length }} row(s) currently in the table? This only
        affects the local working set — already-saved authors in the pool are
        not deleted.
    </p>

    <ng-template #footer>
        <p-button
            label="Cancel"
            severity="secondary"
            [text]="true"
            (onClick)="cancelClear()"
        />
        <p-button
            label="Clear"
            icon="pi pi-trash"
            severity="danger"
            (onClick)="confirmClear()"
        />
    </ng-template>
</p-dialog>

<p-dialog
    [visible]="removeConfirmVisible()"
    (visibleChange)="removeConfirmVisible.set($event)"
    header="Remove this row?"
    [modal]="true"
    [style]="{ width: '28rem' }"
    [draggable]="false"
    [resizable]="false"
>
    <div class="flex flex-col gap-3">
        <p class="m-0 text-sm">
            This row has content that will be lost. @if
            (removeConfirmRow()?.Username?.trim()) {
            <span class="block mt-2 text-xs text-muted-color">
                Username: <strong>{{ removeConfirmRow()!.Username }}</strong>
            </span>
            }
        </p>

        <div class="flex items-center gap-2">
            <p-checkbox
                inputId="author-remove-dont-ask"
                [binary]="true"
                [ngModel]="removeConfirmDontAsk()"
                (ngModelChange)="removeConfirmDontAsk.set($event)"
            />
            <label for="author-remove-dont-ask" class="text-sm cursor-pointer">
                Don't ask in this session
            </label>
        </div>
    </div>

    <ng-template #footer>
        <p-button
            label="Cancel"
            severity="secondary"
            [text]="true"
            (onClick)="cancelRemoveRow()"
        />
        <p-button
            label="Remove"
            icon="pi pi-trash"
            severity="danger"
            (onClick)="confirmRemoveRow()"
        />
    </ng-template>
</p-dialog>

<p-dialog
    [visible]="editDialogVisible()"
    (visibleChange)="editDialogVisible.set($event)"
    header="Edit author"
    [modal]="true"
    [style]="{ width: '32rem' }"
    [draggable]="false"
    [resizable]="false"
    (onHide)="closeEdit()"
>
    @if (editForm(); as form) {
    <div class="flex flex-col gap-3">
        @if (editError()) {
        <p-message
            severity="error"
            [text]="editError()!"
            styleClass="m-0"
        />
        }

        <div class="flex flex-col gap-1">
            <label class="text-sm font-medium" for="edit-author-username">
                Username
            </label>
            <input
                pInputText
                id="edit-author-username"
                type="text"
                [ngModel]="form.Username"
                (ngModelChange)="updateEditUsername($event ?? '')"
            />
        </div>

        <div class="flex flex-col gap-1">
            <label class="text-sm font-medium" for="edit-author-avatar">
                Avatar URL
            </label>
            <div class="flex items-center gap-2">
                <div
                    class="w-10 h-10 shrink-0 rounded-full overflow-hidden border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 flex items-center justify-center"
                >
                    @if (form.AvatarUrl.trim()) {
                    <img
                        [src]="form.AvatarUrl.trim()"
                        alt="Avatar preview"
                        class="w-full h-full object-cover"
                        referrerpolicy="no-referrer"
                        (error)="onAvatarError($event)"
                    />
                    } @else {
                    <i class="pi pi-user text-muted-color text-xs"></i>
                    }
                </div>
                <input
                    pInputText
                    id="edit-author-avatar"
                    type="text"
                    class="flex-1"
                    placeholder="https://… (leave empty to clear)"
                    [ngModel]="form.AvatarUrl"
                    (ngModelChange)="updateEditAvatar($event ?? '')"
                />
            </div>
        </div>

        <div class="flex items-center gap-2">
            <p-checkbox
                inputId="edit-author-active"
                [binary]="true"
                [ngModel]="form.IsActive"
                (ngModelChange)="updateEditActive($event)"
            />
            <label
                for="edit-author-active"
                class="text-sm cursor-pointer"
            >
                Active
            </label>
        </div>
    </div>
    }

    <ng-template #footer>
        <p-button
            label="Cancel"
            severity="secondary"
            [text]="true"
            [disabled]="editSaving()"
            (onClick)="closeEdit()"
        />
        <p-button
            label="Save"
            icon="pi pi-check"
            [loading]="editSaving()"
            (onClick)="saveEdit()"
        />
    </ng-template>
</p-dialog>
